import { Component, computed, inject, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AvContentContainerComponent } from '../../../../Shared/Components/av-content-container/av-content-container.component';
import { AvTableCard } from '../../../../Shared/Components/av-table-card/av-table-card.component';
import { AvTableColumn, AvTableRow } from '../../../../Shared/Interfaces/av-table-card.interface';
import { AutopilotDevicesFacade } from '../../../../../Facades/Intune/autopilot-devices.facade';
import { AutopilotImportHistoryEntry } from '../../../../../Interfaces/api/Intune/autopilot-devices.interface';
import { ImportHistoryDetailDialogComponent } from '../../Partials/import-history-detail-dialog/import-history-detail-dialog.component';
import { ImportHistoryDetailDialogData } from '../../../Interfaces/import-history-detail-dialog.interface';
import { COMMON_IMPORTS } from '../../../../Shared/common-imports';
import { PERMISSIONS } from '../../../../../Enums/permission.enum';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from '../../../../Shared/Services/auth.service';

@Component({
  selector: 'av-autopilot-devices-history',
  standalone: true,
  imports: [COMMON_IMPORTS, AvContentContainerComponent, AvTableCard],
  templateUrl: './autopilot-devices-history.component.html',
  styleUrls: ['./autopilot-devices-history.component.scss'],
})
export class AutopilotDevicesHistoryComponent implements OnInit {
  private readonly _facade = inject(AutopilotDevicesFacade);
  private readonly _dialog = inject(MatDialog);
  private readonly _authService = inject(AuthService);
  readonly currentUser = toSignal(this._authService.currentUser$, { initialValue: null });
  permissions = PERMISSIONS;

  ngOnInit(): void {
    this.loadInitialHistory();
  }

  private loadInitialHistory(): void {
    this.loadHistory();
  }

  readonly isLoading = this._facade.importHistoryLoading;
  readonly error = computed(() =>
    this._facade.importHistoryError() ? 'app.autopilot_devices.history.load_error' : null,
  );
  readonly entries = this._facade.importHistoryEntries;

  readonly tableColumns: AvTableColumn[] = [
    { key: 'index', label: 'app.autopilot_devices.history.columns.index', sortable: false },
    { key: 'action', label: 'app.autopilot_devices.history.columns.action', sortable: false },
    { key: 'status', label: 'app.autopilot_devices.history.columns.status', sortable: false },
    { key: 'devices', label: 'app.autopilot_devices.history.columns.devices', sortable: false },
    { key: 'date', label: 'app.autopilot_devices.history.columns.date', sortable: true },
    {
      key: 'actions',
      label: 'app.autopilot_devices.history.columns.actions',
      type: 'link',
      sortable: false,
    },
  ];

  readonly tableRows = computed<AvTableRow[]>(() => {
    const list = this.entries();
    return list.map((entry, i) => this.entryToRow(entry, i + 1));
  });

  loadHistory(): void {
    if (!this.currentUser()?.hasPermission(PERMISSIONS.AUTOPILOT_DEVICES_IMPORT)) {
      return;
    }
    this._facade.reloadImportHistory();
  }

  openDetailDialog(entry: AutopilotImportHistoryEntry): void {
    this._dialog.open(ImportHistoryDetailDialogComponent, {
      data: { entry } satisfies ImportHistoryDetailDialogData,
      panelClass: 'av-dialog',
    });
  }

  private entryToRow(entry: AutopilotImportHistoryEntry, index: number): AvTableRow {
    const deviceCount = entry.metadata?.import_ids?.length ?? 0;
    const statusKey = `app.autopilot_devices.history.statuses.${entry.metadata?.status ?? 'unknown'}`;
    const actionKey = `app.autopilot_devices.history.actions.${entry.action}`;
    const date = entry.created_at ? new Date(entry.created_at).toLocaleString() : '';

    return {
      index,
      action: actionKey,
      status: statusKey,
      devices: deviceCount,
      date,
      actions: {
        icon: 'visibility',
        ariaLabel: 'app.autopilot_devices.history.view_details',
        onClick: () => this.openDetailDialog(entry),
      },
    };
  }
}
